import { FrequencyType } from '../types/common';
import { DepartmentItem } from '../types/category';
import { httpClient, QueryParams } from './httpClient';

// 模版类型映射
export const templateTypeMap: Record<string, string> = {
  System: '基础模板',
  Custom: '自定义模板',
};

// 应用项目映射
export const sourceMap: Record<string, string> = {
  KPI: '核心指标年度计划表',
  Report: '报表',
  Business: '经营会计报表',
  Budget: '年度计划表',
};

// 报表类型映射
export const frequencyTypeMap: Record<string, string> = {
  [FrequencyType.Yearly]: '年报表',
  [FrequencyType.Monthly]: '月报表',
  [FrequencyType.Quarterly]: '季度报表',
  [FrequencyType.Weekly]: '周报表',
};

// 报表应用类型枚举
export enum ReportType {
  KPI = 'KPI',
  Report = 'Report', 
  Business = 'Business',
  Budget = 'Budget',
}

// 报表应用类型映射
export const reportTypeMap: Record<string, string> = {
  [ReportType.KPI]: '核心指标年度计划表',
  [ReportType.Report]: '报表',
  [ReportType.Business]: '经营会计报表',
  [ReportType.Budget]: '年度计划表',
};

// 模版类型选项
export const templateTypeOptions = [
  { value: 'System', label: '基础模板' },
  { value: 'Custom', label: '自定义模板' },
];

// 应用项目选项
export const sourceOptions = [
  { value: 'KPI', label: sourceMap.KPI },
  { value: 'Report', label: sourceMap.Report },
  { value: 'Business', label: sourceMap.Business },
  { value: 'Budget', label: sourceMap.Budget },
];

// 报表类型选项
export const frequencyTypeOptions = [
  { value: FrequencyType.Yearly, label: frequencyTypeMap[FrequencyType.Yearly] },
  { value: FrequencyType.Monthly, label: frequencyTypeMap[FrequencyType.Monthly] },
  { value: FrequencyType.Quarterly, label: frequencyTypeMap[FrequencyType.Quarterly] },
  { value: FrequencyType.Weekly, label: frequencyTypeMap[FrequencyType.Weekly] },
];

export interface User {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  pinyinAbbreviation: string | null;
  email: string;
  mobile: string;
  createdAt: string | null;
  updatedAt: string;
  avatar: string;
  dateOfBirth: string;
  gender: string;
}

export interface Department {
  id: string;
  name: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

// 表头设置接口
export interface ColumnSetting {
  title: string;
  subtitle?: string;
  key: string;
  order: number;
}

export interface DisplayRows {
  key: string;
  title: string;
}

export interface RowSetting {
  rowId: number;
  subjectId: string;
  isMultipleRows: boolean;
  displayRows: DisplayRows[];
  backgroundColor: string;
}

export interface Template {
  id: string;
  creator: User;
  lastUpdater: User;
  createdAt: string;
  updatedAt: string;
  code: string;
  type: string;
  frequencyType: string;
  source: string;
  departments: Department[];
  name: string | null;
  description: string | null;
  enabled: boolean;
  columnSettings: ColumnSetting[];
  rowSettings: RowSetting[];
  reportType?: "Business" | "Report" | "Budget" | "KPI";
  systemTemplateId?: string;
  kpiSettings?: {
    lastMonthPlan: boolean;
    totalRollingPlan: boolean;
    difference: boolean;
  };
}

export interface TemplateList {
  total: number;
  size: number;
  pages: number;
  page: number;
  records: Template[];
}

export interface GetAllTemplatesParams {
  page?: number;
  size?: number;
  name?: string;
  type?: string;
  frequencyType?: string;
  source?: string;
  enabled?: boolean;
  departmentId?: string;
  startDate?: string;
  endDate?: string;
  assessmentDimensionId?: string;
  subjectId?: string;
}

export interface EnabledTemplate {
  departmentId: string;
  source: string;
  frequencyType: FrequencyType;
  reportType: 'Report' | 'KPI' | 'Budget' | 'Business';
}

export interface FilterTemplate {
  name?: string;
  description?: string;
  frequencyType?: FrequencyType;
  source?: string;
  reportType?: string;
  enabled?: boolean;
  createdAt?: {
    min?: string;
    max?: string;
  };
  departmentIds?: string[];
  assessmentDimensionId?: string;
  subjectId?: string;
}

export async function getAllTemplates(params?: GetAllTemplatesParams): Promise<TemplateList> {
  const queryParams: QueryParams = {};

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams[key] = String(value);
      }
    });
  }

  const response = await httpClient.sendRequest({
    url: 'templates',
    method: 'GET',
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
  });

  return response.data;
}

export async function getTemplate(id: string): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: `templates/${id}`,
    method: 'GET',
  });
  return response.data;
}

// 创建模板的请求参数接口
export interface CreateTemplateParams { 
  source: string; // 应用项目
  frequencyType: FrequencyType; // 报表类型
  type: 'System' | 'Custom'; // 模板类型
  name: string; // 模板名称
  description?: string; // 模板说明
  enabled: boolean; // 是否启用
  departments: Array<string | DepartmentItem | Department>; // 作用部门ID数组或部门对象数组
  columnSettings: ColumnSetting[]; // 表头设置
  rowSettings: RowSetting[]; // 行设置
  reportType?: string;
  systemTemplateId?: string;
  status?: 'Draft' | 'Published'; // 模板状态
  kpiSettings?: {
    lastMonthPlan: boolean;
    totalRollingPlan: boolean;
    difference: boolean;
  };
}

export async function createTemplate(payload: CreateTemplateParams): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: 'templates',
    method: 'POST',
    data: payload,
  });
  return response.data;
}

export async function updateTemplate(id: string, payload: Partial<Template>): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: 'templates',
    method: 'PUT',
    data: { ...payload, id },
  });
  return response.data;
}

export async function deleteTemplate(id: string, payload: Partial<Template>): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: 'templates',
    method: 'DELETE',
    data: { ...payload, id },
  });
  return response.data;
}

export async function toggleTemplateStatus(id: string, enabled: boolean): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: `templates/${id}/status`,
    method: 'PATCH',
    data: { enabled },
  });
  return response.data;
}

// 添加新的API函数，用于启用模板
export async function enableTemplate(id: string): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: `templates/${id}/enable`,
    method: 'POST',
  });
  return response.data;
} 

export async function disableTemplate(id: string): Promise<Template> {
  const response = await httpClient.sendRequest({
    url: `templates/${id}/disable`,
    method: 'POST',
  });
  return response.data;
}

export async function getSystemTemplates(): Promise<Template[]> {

  const response = await httpClient.sendRequest({
    url: 'templates/system',
    method: 'GET',
  });

  return response.data;
}

export async function getEnabledTemplate(params?: EnabledTemplate): Promise<Template | null> {
  const queryParams: QueryParams = {};

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams[key] = String(value);
      }
    });
  }

  try {
    const response = await httpClient.sendRequest({
      url: 'templates/enabled',
      method: 'GET',
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
    });

    return response.data;
  } catch (error) {
          // 错误处理
    return null;
  }
}

export async function filterTemplates(payload: FilterTemplate, params?: GetAllTemplatesParams): Promise<Template[]> {
  const queryParams: Record<string, string> = {};
  
  if (params?.page !== undefined) {
    queryParams.page = params.page.toString();
  }
  
  if (params?.size !== undefined) {
    queryParams.size = params.size.toString();
  }
  
  const response = await httpClient.sendRequest({
    url: 'templates/search',
    method: 'POST',
    data: payload,
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
  });
  return response.data;
}
